import { memo, useCallback, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, IconButton, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { faCircleInfo, faHeart as faHeartBorder } from "@fortawesome/pro-regular-svg-icons"
import { faHeart } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { enqueueSnackbar } from "notistack"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { ProductDisabledCover } from "../../../../productAvailability/ProductDisabledCover"
import { useProductStock } from "../../../../productAvailability/useProductStock"
import { SearchableText } from "../../../../search/Searchable"
import { OfferTag } from "../../shop/product/OfferTag"
import { ProductCounter } from "../../shop/product/ProductCounter"
import { Tag } from "../../shop/product/Tag"
import ProductImg from "../../shop/product/ProductImg"

import type { TakeawayProduct } from "../takeawayTypes"
import { useApiImageUrl } from "../useApiImageUrl"

const CARD_BORDER_RADIUS = "1rem"

const useStyles = makeStyles()((theme) => ({
    container: {
        width: "14rem",
        position: "relative",
        overflow: "hidden",
        borderRadius: CARD_BORDER_RADIUS,
        background: theme.palette.common.white,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
    cardContent: {
        padding: ".75rem",
        color: theme.palette.text.primary,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "7.75rem",
    },
    tagLine: {
        display: "flex",
        alignItems: "center",
        gap: ".375rem",
        flexWrap: "wrap",
    },
    onlyLeftTag: {
        display: "inline-flex",
        alignItems: "center",
        padding: `0 ${theme.spacing(1)}`,
        color: theme.palette.common.white,
        backgroundColor: theme.palette.common.black,
        borderRadius: ".25rem",
    },
    productTitle: {
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,
        overflow: "hidden",
        minHeight: "3rem",
        lineHeight: "1.5rem",
        overflowWrap: "anywhere",
        wordBreak: "normal",
    },
    iconWrap: {
        display: "inline-flex",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
    },
    infoButton: {
        padding: "0",
        color: theme.palette.tailwind.slate[400],
        transition: "all 0.3s ease-out",
        marginLeft: ".25rem",
        fontSize: "1rem",
        lineHeight: "1.5rem",
        "&:hover": {
            color: theme.palette.tailwind.slate[500],
        },
    },
    favoriteButton: {
        position: "absolute",
        top: ".75rem",
        right: ".75rem",
        padding: ".375rem",
        background: alpha(theme.palette.common.white, 0.5),
        color: theme.palette.tailwind.blue[600],
        zIndex: 2,
        transition: "all 0.3s ease-out",
        "&:hover": {
            background: theme.palette.common.white,
            color: theme.palette.tailwind.blue[800],
        },
    },
    counterRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: ".5rem",
    },
}))

type TakeawayProductCardProps = Readonly<{
    product: TakeawayProduct
    quantity: number
    isFavorite: boolean
    searchRegex?: RegExp
    onQuantityChange: (product: TakeawayProduct, quantity: number) => void
    onFavoriteToggle: (product: TakeawayProduct, makeFavorite: boolean) => Promise<boolean>
    onDetailsClick: (product: TakeawayProduct) => void
}>

export const TakeawayProductCard = memo(function TakeawayProductCard({
    product,
    quantity,
    isFavorite,
    searchRegex,
    onQuantityChange,
    onFavoriteToggle,
    onDetailsClick,
}: TakeawayProductCardProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})
    const resolvedImageUrl = useApiImageUrl(product.imageUrl)
    const [isFavoriteLoading, setIsFavoriteLoading] = useState(false)

    const availability = product.orderDetails?.availability
    const isUnavailable = availability !== undefined && !availability.isAvailable
    const { disableIncrease, productsLeftCount } = useProductStock({
        stock: product.stock,
        currentQuantity: quantity,
        isUnavailable,
    })

    const price = product.price ? moneyFactory.formatAsNumber(toDinero(product.price)) : null
    const marketingTag = product.productDescription?.marketingTag
    const canFavorite = product.userDetails?.canFavorite
    const showTagLine = Boolean(marketingTag || productsLeftCount)

    const handleFavoriteClick = useCallback(async (e: React.MouseEvent) => {
        e.stopPropagation()
        if (isFavoriteLoading) return
        setIsFavoriteLoading(true)
        await onFavoriteToggle(product, !isFavorite)
        setIsFavoriteLoading(false)
    }, [product, isFavorite, isFavoriteLoading, onFavoriteToggle])

    return (
        <Box className={classes.container}>
            <ProductDisabledCover availability={availability} />

            <Box position="relative">
                <ProductImg imageUrl={resolvedImageUrl ?? undefined} />
                <OfferTag top=".75rem" left=".75rem" offerDetails={product.offerDetails} size="sm" />
                {canFavorite && (
                    <IconButton
                        className={classes.favoriteButton}
                        size="small"
                        disabled={isFavoriteLoading}
                        onClick={handleFavoriteClick}
                    >
                        <FontAwesomeIcon icon={isFavorite ? faHeart : faHeartBorder} />
                    </IconButton>
                )}
            </Box>

            <Stack gap=".375rem" className={classes.cardContent}>
                <Box>
                    {showTagLine && (
                        <Box className={classes.tagLine}>
                            {marketingTag && <Tag>{marketingTag}</Tag>}
                            {!!productsLeftCount && (
                                <Box className={classes.onlyLeftTag}>
                                    <LocalizedStrict id="offer-tag-limited-on-sale" vars={{ amount: productsLeftCount }}>
                                        <Typography variant="caption">{productsLeftCount} left</Typography>
                                    </LocalizedStrict>
                                </Box>
                            )}
                        </Box>
                    )}
                    <SearchableText search={searchRegex}>
                        <Typography variant="subtitle1" className={classes.productTitle}>
                            {product.name}
                            <Typography className={classes.iconWrap}>
                                <IconButton
                                    onClick={() => onDetailsClick(product)}
                                    className={classes.infoButton}
                                    size="small"
                                >
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </IconButton>
                            </Typography>
                        </Typography>
                    </SearchableText>
                </Box>

                <Box className={classes.counterRow}>
                    {price && <Typography variant="body1">{price}</Typography>}
                    <ProductCounter
                        count={quantity}
                        handleCounter={(qty) => onQuantityChange(product, qty)}
                        disableIncrease={disableIncrease}
                        onDisabledClick={() =>
                            enqueueSnackbar(l10n.getString("takeaway-product-sold-out-snackbar"), { variant: "warning" })
                        }
                    />
                </Box>
            </Stack>
        </Box>
    )
})
